"use client";
import React from "react";
import dayjs from "dayjs";
import Button from "antd/es/button";
import { DEFAULT_DATE_FORMAT } from "@utils/core";
import {
  dateInputContainerStyle,
  dateInputButtonStyle,
  dateInputLabelStyle,
} from "../../bookingStyles";

const LeftIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M724 218.3V141c0-6.7-7.7-10.4-12.9-6.3L260.3 486.8a31.86 31.86 0 000 50.3l450.8 352.1c5.3 4.1 12.9.4 12.9-6.3v-77.3c0-4.9-2.3-9.6-6.1-12.6l-360-281 360-281.1c3.8-3 6.1-7.7 6.1-12.6z" />
  </svg>
);

const RightIcon = () => (
  <svg viewBox="64 64 896 896" width="1em" height="1em" fill="currentColor">
    <path d="M765.7 486.8L314.9 134.7A7.97 7.97 0 00302 141v77.3c0 4.9 2.3 9.6 6.1 12.6l360 281.1-360 281.1c-3.9 3-6.1 7.7-6.1 12.6V883c0 6.7 7.7 10.4 12.9 6.3l450.8-352.1a31.96 31.96 0 000-50.3z" />
  </svg>
);

interface CustomDateInputProps {
  value: string;
  selectedDate?: Date;
  /**
   * The location's today (`DateSelector`), or `null` until it is known:
   * `null` on the server and during hydration, so no clock-derived text
   * reaches the server markup (#553).
   */
  today: Date | null;
  displayLabel?: string;
  onClick: () => void;
  onPrev: () => void;
  onNext: () => void;
  hasBorder?: boolean;
  onBlur?: React.FocusEventHandler<HTMLButtonElement>;
  onFocus?: React.FocusEventHandler<HTMLButtonElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  tabIndex?: number;
  disabled?: boolean;
  /** The selected day is the last one customers may book. */
  nextDisabled?: boolean;
}

const CustomDateInput = React.forwardRef<
  HTMLButtonElement,
  CustomDateInputProps
>(function CustomDateInput(
  {
    value,
    selectedDate,
    today: todayDate,
    displayLabel,
    onClick,
    onPrev,
    onNext,
    onBlur,
    onFocus,
    onKeyDown,
    tabIndex,
    disabled,
    nextDisabled,
  },
  ref,
) {
  const today = todayDate ? dayjs(todayDate) : null;
  const selectedDay = selectedDate
    ? dayjs(selectedDate).startOf("day")
    : dayjs(value, DEFAULT_DATE_FORMAT).startOf("day");
  const diffDays = !today
    ? null
    : selectedDate
      ? today.diff(selectedDay, "days")
      : value === today.format(DEFAULT_DATE_FORMAT)
        ? 0
        : today.diff(selectedDay, "days");
  const isToday = diffDays === 0;
  // Today is the earliest bookable day. A selection can sit before it (a
  // fixed start day, or a page left open past the location's midnight), so
  // Previous stops at today or earlier, not only on today itself.
  const onOrBeforeToday = diffDays !== null && diffDays >= 0;

  let label = value;
  if (isToday) {
    label = "Today";
  } else if (diffDays === 1) {
    label = "Yesterday";
  } else if (diffDays === -1) {
    label = "Tomorrow";
  }
  // The selected day is seeded from the clock too, so before the location's
  // day is known the control shows no date and stays inert.
  const visibleLabel = today ? (displayLabel ?? label) : "";
  const awaitingToday = !today;

  return (
    <>
      <style>
        {`.booking-date-input {
            transition: border-color 0.2s;
          }
          .booking-date-input:hover {
            border-color: var(--openings-booking-control-hover-border, var(--openings-color-primary, #111)) !important;
          }
          .booking-date-input:focus-within {
            border-color: var(--openings-booking-control-hover-border, var(--openings-color-primary, #111)) !important;
          }
          .booking-date-input button:focus-visible {
            position: relative;
            z-index: 1;
            outline: 2px solid var(--openings-color-primary, #111);
            outline-offset: -2px;
          }`}
      </style>
      <div className="booking-date-input" style={dateInputContainerStyle}>
        <Button
          htmlType="button"
          className="booking-date-input-nav booking-date-input-nav--previous"
          style={dateInputButtonStyle}
          disabled={disabled || awaitingToday || onOrBeforeToday}
          icon={<LeftIcon />}
          onClick={onPrev}
          aria-label="Previous day"
        />
        <button
          ref={ref}
          type="button"
          className="booking-date-input-label"
          style={{
            ...dateInputLabelStyle,
            alignSelf: "stretch",
            border: 0,
            background: "transparent",
            fontFamily: "inherit",
          }}
          onClick={onClick}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={(event) => {
            // A native button already turns Enter/Space into click. Passing
            // those keys to react-datepicker as well toggles the popover
            // twice, so forward only its non-activation keyboard handling.
            if (event.key !== "Enter" && event.key !== " ") {
              onKeyDown?.(event);
            }
          }}
          tabIndex={tabIndex}
          disabled={disabled || awaitingToday}
          aria-label={
            visibleLabel
              ? `Open date picker, selected date ${visibleLabel}`
              : "Open date picker"
          }
        >
          {visibleLabel}
        </button>
        <Button
          htmlType="button"
          className="booking-date-input-nav booking-date-input-nav--next"
          style={dateInputButtonStyle}
          disabled={disabled || awaitingToday || nextDisabled}
          icon={<RightIcon />}
          onClick={onNext}
          aria-label="Next day"
        />
      </div>
    </>
  );
});

CustomDateInput.displayName = "CustomDateInput";

export default CustomDateInput;
